/**
 * Agent Prompts Settings Page - Configuration for Agent System Instructions
 * Master Role Only
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Fade,
  Grid,
} from '@mui/material';
import {
  Psychology as PromptIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.apiUrl;

export default function AgentPromptsSettingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);

  // Form state
  const [agentName, setAgentName] = useState('host');
  const [promptInstruction, setPromptInstruction] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [promptsList, setPromptsList] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Agent options
  const agents = [
    { value: 'host', label: t('agents.host'), icon: '🏢', color: '#667eea' },
    { value: 'sales', label: t('agents.sales'), icon: '💼', color: '#f093fb' },
    { value: 'hr', label: t('agents.hr'), icon: '👥', color: '#4facfe' },
    { value: 'guest', label: t('agents.guest'), icon: '👤', color: '#43e97b' },
  ];

  // Load prompts list
  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setLoadingList(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/settings/agent-prompts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPromptsList(response.data.prompts || []);
    } catch (error) {
      console.error('Error loading prompts:', error);
      showSnackbar(t('agentPrompts.errorLoading', { ns: 'settings' }), 'error');
    } finally {
      setLoadingList(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!promptInstruction.trim()) {
      showSnackbar(t('agentPrompts.errorEmptyPrompt', { ns: 'settings' }), 'error');
      return;
    }

    if (promptInstruction.trim().length < 10) {
      showSnackbar(t('agentPrompts.errorMinLength', { ns: 'settings' }), 'error');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        agent_name: agentName,
        prompt_instruction: promptInstruction,
        is_active: true,
        metadata: {},
      };

      if (editMode && editingId) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/api/v1/settings/agent-prompts/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar(t('agentPrompts.successUpdate', { ns: 'settings' }), 'success');
      } else {
        // Create new
        await axios.post(`${API_BASE_URL}/api/v1/settings/agent-prompts`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar(t('agentPrompts.successCreate', { ns: 'settings' }), 'success');
      }

      // Reset form
      resetForm();
      loadPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);

      // Parse FastAPI validation errors
      let errorMsg = t('agentPrompts.errorSaving', { ns: 'settings' });

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // Check if detail is array (FastAPI validation error)
        if (Array.isArray(detail)) {
          const messages = detail.map(err => {
            const field = err.loc?.join(' > ') || 'unknown';
            return `${err.msg || 'Invalid input'} (${field})`;
          });
          errorMsg = messages.join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail.message) {
          errorMsg = detail.message;
        }
      } else if (error.response?.status === 401) {
        errorMsg = t('errors.common.unauthorized');
      } else if (error.response?.status === 403) {
        errorMsg = t('errors.common.forbidden');
      } else if (error.message) {
        errorMsg = error.message;
      }

      showSnackbar(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setAgentName(item.agent_name);
    setPromptInstruction(item.prompt_instruction);
    setEditMode(true);
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    const { id } = deleteDialog;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE_URL}/api/v1/settings/agent-prompts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar(t('agentPrompts.successDelete', { ns: 'settings' }), 'success');
      loadPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
      showSnackbar(t('agentPrompts.errorDeleting', { ns: 'settings' }), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const resetForm = () => {
    setAgentName('host');
    setPromptInstruction('');
    setEditMode(false);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const getAgentInfo = (agentNameValue) => {
    return agents.find((a) => a.value === agentNameValue) || agents[0];
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header - Modern Design */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            right: 100,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <IconButton 
            onClick={() => navigate('/settings')} 
            sx={{ 
              mr: 2, 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <BackIcon />
          </IconButton>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <PromptIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="600">{t('agentPrompts.pageTitle', { ns: 'settings' })}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t('agentPrompts.pageSubtitle', { ns: 'settings' })} • {promptsList.length} {t('agentPrompts.promptsConfigured', { ns: 'settings' })}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 0 }}>

        <Grid container spacing={4}>
          {/* Configuration Form */}
          <Grid item xs={12} lg={6}>
            <Fade in timeout={400}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  height: '100%',
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {editMode ? t('agentPrompts.editPrompt', { ns: 'settings' }) : t('agentPrompts.newPrompt', { ns: 'settings' })}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Agent Selection */}
                    <FormControl fullWidth>
                      <InputLabel>{t('agentPrompts.agent', { ns: 'settings' })}</InputLabel>
                      <Select
                        value={agentName}
                        label={t('agentPrompts.agent', { ns: 'settings' })}
                        onChange={(e) => setAgentName(e.target.value)}
                      >
                        {agents.map((agent) => (
                          <MenuItem key={agent.value} value={agent.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{agent.icon}</span>
                              <span>{agent.label}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Prompt Instruction Input */}
                    <TextField
                      fullWidth
                      multiline
                      rows={24}
                      minRows={16}
                      label={t('agentPrompts.promptInstruction', { ns: 'settings' })}
                      value={promptInstruction}
                      onChange={(e) => setPromptInstruction(e.target.value)}
                      required
                      error={promptInstruction.length > 0 && promptInstruction.length < 10}
                      placeholder={t('agentPrompts.promptPlaceholder', { ns: 'settings' })}
                      sx={{
                        '& .MuiInputBase-root': {
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                          lineHeight: 1.6,
                        },
                      }}
                      helperText={
                        promptInstruction.length > 0 && promptInstruction.length < 10
                          ? t('agentPrompts.charCountError', { ns: 'settings', count: promptInstruction.length })
                          : t('agentPrompts.charCount', { ns: 'settings', count: promptInstruction.length })
                      }
                    />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        disabled={loading}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '1rem',
                        }}
                      >
                        {editMode ? t('common.update') : t('common.save')}
                      </Button>

                      {editMode && (
                        <Button
                          variant="outlined"
                          onClick={handleCancelEdit}
                          startIcon={<CancelIcon />}
                          sx={{ py: 1.5 }}
                        >
                          {t('common.cancel')}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </form>
              </Paper>
            </Fade>
          </Grid>

          {/* Prompts List */}
          <Grid item xs={12} lg={6}>
            <Fade in timeout={500}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  maxHeight: 900,
                  overflow: 'auto',
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  {t('agentPrompts.configuredPrompts', { ns: 'settings' })}
                </Typography>

                {loadingList ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : promptsList.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <BotIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('agentPrompts.noPrompts', { ns: 'settings' })}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {promptsList.map((item) => {
                      const agentInfo = getAgentInfo(item.agent_name);
                      return (
                        <Card
                          key={item.id}
                          sx={{
                            border: item.is_active ? `2px solid ${agentInfo.color}` : '1px solid #e0e0e0',
                            borderRadius: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            },
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: agentInfo.color }}>
                                    {agentInfo.icon} {agentInfo.label}
                                  </Typography>
                                  {item.is_active && (
                                    <Chip
                                      icon={<CheckIcon sx={{ fontSize: 14 }} />}
                                      label="Active"
                                      size="small"
                                      color="success"
                                      sx={{ height: 22 }}
                                    />
                                  )}
                                  <Chip
                                    label={`v${item.version}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 22 }}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    bgcolor: '#f5f5f5',
                                    p: 1,
                                    borderRadius: 1,
                                    my: 1,
                                  }}
                                >
                                  {item.prompt_instruction}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.prompt_instruction.length} {t('agentPrompts.chars', { ns: 'settings' })} • {t('agentPrompts.updated', { ns: 'settings' })}: {new Date(item.updated_at).toLocaleString()}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                <Tooltip title={t('common.edit')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(item)}
                                    sx={{ color: agentInfo.color }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('common.delete')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => setDeleteDialog({ open: true, id: item.id })}
                                    sx={{ color: '#f44336' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        {/* Dynamic Variables Info */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mt: 3,
              borderRadius: 2,
              border: '1px solid #e3f2fd',
              bgcolor: '#f8fbff',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#1565c0' }}>
              📌 {t('agentPrompts.dynamicVariables.title', { ns: 'settings' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('agentPrompts.dynamicVariables.description', { ns: 'settings' })}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7b1fa2', fontWeight: 600 }}>
                  {'{_format_tools_section(tools)}'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('agentPrompts.dynamicVariables.tools', { ns: 'settings' })}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7b1fa2', fontWeight: 600 }}>
                  {'{agents}'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('agentPrompts.dynamicVariables.agents', { ns: 'settings' })}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7b1fa2', fontWeight: 600 }}>
                  {'{datetime.now().strftime("%Y-%m-%d")}'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('agentPrompts.dynamicVariables.datetime', { ns: 'settings' })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Info Alert */}
        <Fade in timeout={700}>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              💡 <strong>{t('agentPrompts.noteTitle', { ns: 'settings' })}:</strong> {t('agentPrompts.noteMessage', { ns: 'settings' })}
            </Typography>
          </Alert>
        </Fade>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          ⚠️ {t('agentPrompts.confirmDelete', { ns: 'settings' })}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">{t('agentPrompts.confirmDeleteMessage', { ns: 'settings' })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })} sx={{ borderRadius: 2 }}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ borderRadius: 2 }}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

